/* =====================================================================
 * useTableCellMenu — 노션 스타일 행/열 선택 핸들 + 드롭다운 메뉴
 *
 * - 테이블 호버 시 각 열 상단 / 행 좌측에 선택 핸들 표시
 * - 핸들 클릭 시 행/열 선택 + 컨텍스트 메뉴 (색, 삽입, 복사, 삭제)
 * - 색상 변경 서브메뉴 (텍스트 색상 + 배경 색상)
 * - 복사 시 docx 호환 HTML 테이블 형태로 클립보드에 저장
 * ===================================================================== */

import { useEffect, useCallback, useState, useRef, createElement } from 'react';
import type { Editor } from '@tiptap/react';
import { CellSelection } from '@tiptap/pm/tables';
import type { DropdownMenuItem } from '@auw/workspace-ui';
import {
  Palette,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Copy,
  XCircle,
  Trash2,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/* 타입 / 상수                                                          */
/* ------------------------------------------------------------------ */

export interface CellMenuState {
  open: boolean;
  type: 'row' | 'column';
  /** 행/열 인덱스 (0-based) */
  index: number;
  /** 드롭다운 X 좌표 (fixed) */
  x: number;
  /** 드롭다운 Y 좌표 (fixed) */
  y: number;
  direction: 'down' | 'up';
  /** ProseMirror 상에서 테이블 노드의 시작 위치 */
  tablePos: number;
}

const TEXT_COLORS = [
  { id: 'default', label: '기본 텍스트', color: '' },
  { id: 'gray', label: '회색 텍스트', color: '#9ca3af' },
  { id: 'brown', label: '갈색 텍스트', color: '#a16207' },
  { id: 'orange', label: '주황색 텍스트', color: '#ea580c' },
  { id: 'yellow', label: '노란색 텍스트', color: '#ca8a04' },
  { id: 'green', label: '초록색 텍스트', color: '#16a34a' },
  { id: 'blue', label: '파란색 텍스트', color: '#2563eb' },
  { id: 'purple', label: '보라색 텍스트', color: '#9333ea' },
  { id: 'pink', label: '분홍색 텍스트', color: '#db2777' },
  { id: 'red', label: '빨간색 텍스트', color: '#dc2626' },
];

const BG_COLORS = [
  { id: 'default', label: '기본 배경', color: '' },
  { id: 'gray', label: '회색 배경', color: '#f3f4f6' },
  { id: 'brown', label: '갈색 배경', color: '#fef3c7' },
  { id: 'orange', label: '주황색 배경', color: '#ffedd5' },
  { id: 'yellow', label: '노란색 배경', color: '#fef9c3' },
  { id: 'green', label: '초록색 배경', color: '#dcfce7' },
  { id: 'blue', label: '파란색 배경', color: '#dbeafe' },
  { id: 'purple', label: '보라색 배경', color: '#f3e8ff' },
  { id: 'pink', label: '분홍색 배경', color: '#fce7f3' },
  { id: 'red', label: '빨간색 배경', color: '#fee2e2' },
];

/* 6-dot grip SVG (세로 방향 — 열 핸들용) */
const GRIP_ICON_VERTICAL = `<svg width="14" height="6" viewBox="0 0 14 6" fill="currentColor">
  <circle cx="3" cy="1.5" r="1"/>
  <circle cx="7" cy="1.5" r="1"/>
  <circle cx="11" cy="1.5" r="1"/>
  <circle cx="3" cy="4.5" r="1"/>
  <circle cx="7" cy="4.5" r="1"/>
  <circle cx="11" cy="4.5" r="1"/>
</svg>`;

/* 6-dot grip SVG (가로 방향 — 행 핸들용) */
const GRIP_ICON_HORIZONTAL = `<svg width="6" height="14" viewBox="0 0 6 14" fill="currentColor">
  <circle cx="1.5" cy="3" r="1"/>
  <circle cx="4.5" cy="3" r="1"/>
  <circle cx="1.5" cy="7" r="1"/>
  <circle cx="4.5" cy="7" r="1"/>
  <circle cx="1.5" cy="11" r="1"/>
  <circle cx="4.5" cy="11" r="1"/>
</svg>`;

/* ------------------------------------------------------------------ */
/* 유틸리티                                                             */
/* ------------------------------------------------------------------ */

/** 테이블 DOM에서 ProseMirror 위치를 찾는다 */
function findTablePos(editor: Editor, tableEl: HTMLTableElement): number | null {
  let found: number | null = null;
  editor.state.doc.descendants((node, pos) => {
    if (found !== null) return false;
    if (node.type.name === 'table') {
      const dom = editor.view.nodeDOM(pos);
      if (dom === tableEl || (dom as HTMLElement)?.querySelector?.('table') === tableEl) {
        found = pos;
        return false;
      }
    }
    return true;
  });
  return found;
}

/** 특정 행/열의 모든 셀 위치를 가져온다 */
function getCellPositions(
  editor: Editor,
  tablePos: number,
  type: 'row' | 'column',
  index: number,
): number[] {
  const positions: number[] = [];
  const tableNode = editor.state.doc.nodeAt(tablePos);
  if (!tableNode) return positions;

  let rowOffset = tablePos + 1; // table 내부 시작

  for (let rowIdx = 0; rowIdx < tableNode.childCount; rowIdx++) {
    const row = tableNode.child(rowIdx);
    let cellOffset = rowOffset + 1; // row 내부 시작

    for (let colIdx = 0; colIdx < row.childCount; colIdx++) {
      const cell = row.child(colIdx);

      if (type === 'row' && rowIdx === index) {
        positions.push(cellOffset);
      }
      if (type === 'column' && colIdx === index) {
        positions.push(cellOffset);
      }

      cellOffset += cell.nodeSize;
    }

    rowOffset += row.nodeSize;
  }

  return positions;
}

/** 행/열의 셀을 CellSelection으로 선택한다 */
function selectCells(
  editor: Editor,
  tablePos: number,
  type: 'row' | 'column',
  index: number,
) {
  const positions = getCellPositions(editor, tablePos, type, index);
  if (positions.length < 1) return;

  const first = positions[0];
  const last = positions[positions.length - 1];

  try {
    const $anchorCell = editor.state.doc.resolve(first);
    const $headCell = editor.state.doc.resolve(last);
    const selection = CellSelection.create(editor.state.doc, $anchorCell.pos, $headCell.pos);
    const tr = editor.state.tr.setSelection(selection);
    editor.view.dispatch(tr);
  } catch {
    // fallback: 첫 번째 셀에 포커스
    editor.commands.focus(first + 1);
  }
}

/** 행/열의 모든 셀 HTML을 추출하여 docx 호환 HTML 테이블로 반환 */
function buildCopyHtml(
  editor: Editor,
  tablePos: number,
  type: 'row' | 'column',
  index: number,
): string {
  const tableNode = editor.state.doc.nodeAt(tablePos);
  if (!tableNode) return '';

  const rows: string[][] = [];
  let rowOffset = tablePos + 1;

  for (let rowIdx = 0; rowIdx < tableNode.childCount; rowIdx++) {
    const row = tableNode.child(rowIdx);
    const cells: string[] = [];
    let cellOffset = rowOffset + 1;

    for (let colIdx = 0; colIdx < row.childCount; colIdx++) {
      const cell = row.child(colIdx);
      const isTarget =
        (type === 'row' && rowIdx === index) ||
        (type === 'column' && colIdx === index);

      if (isTarget) {
        const cellDom = editor.view.nodeDOM(cellOffset) as HTMLElement | null;
        const content = cellDom ? cellDom.innerHTML : '';
        const tag = cell.type.name === 'tableHeader' ? 'th' : 'td';
        const bgColor = cell.attrs.backgroundColor;
        const style = [
          'border: 1px solid #d1d5db',
          'padding: 8px 12px',
          bgColor ? `background-color: ${bgColor}` : '',
        ].filter(Boolean).join('; ');
        cells.push(`<${tag} style="${style}">${content}</${tag}>`);
      }

      cellOffset += cell.nodeSize;
    }

    if (cells.length > 0) {
      rows.push(cells);
    }

    rowOffset += row.nodeSize;
  }

  if (rows.length === 0) return '';

  const rowsHtml = rows.map((cells) => `<tr>${cells.join('')}</tr>`).join('');
  return `<table style="border-collapse: collapse; width: auto;">${rowsHtml}</table>`;
}

/* ------------------------------------------------------------------ */
/* 색상 아이콘 React 요소 생성                                            */
/* ------------------------------------------------------------------ */

function TextColorIcon({ color }: { color: string }) {
  const c = color || '#1f2937';
  return createElement('svg', { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none' },
    createElement('text', {
      x: 4, y: 11, fontSize: 11, fontWeight: 700, fill: c, fontFamily: 'sans-serif',
    }, 'A'),
  );
}

function BgColorIcon({ color }: { color: string }) {
  const fill = color || 'transparent';
  const stroke = color ? 'none' : '#d1d5db';
  return createElement('svg', { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none' },
    createElement('rect', {
      x: 1, y: 1, width: 14, height: 14, rx: 3, fill, stroke, strokeWidth: 1,
    }),
  );
}

/* ------------------------------------------------------------------ */
/* 훅 본체                                                              */
/* ------------------------------------------------------------------ */

interface UseTableCellMenuOptions {
  editor: Editor | null;
}

export function useTableCellMenu({ editor }: UseTableCellMenuOptions) {
  const [menuState, setMenuState] = useState<CellMenuState>({
    open: false,
    type: 'column',
    index: 0,
    x: 0,
    y: 0,
    direction: 'down',
    tablePos: 0,
  });

  const menuStateRef = useRef(menuState);
  menuStateRef.current = menuState;

  const closeMenu = useCallback(() => {
    setMenuState((prev) => ({ ...prev, open: false }));
  }, []);

  /* ── 행/열 선택 핸들 DOM 생성 및 관리 ── */
  useEffect(() => {
    if (!editor) return;
    const editorDom = editor.view.dom;
    const HANDLES_ATTR = 'data-table-handles';

    /** 핸들 요소 생성 */
    const createHandle = (
      type: 'col' | 'row',
      index: number,
      tableEl: HTMLTableElement,
      wrapper: HTMLElement,
    ) => {
      const handle = document.createElement('div');
      handle.className = type === 'col' ? 'table-col-handle' : 'table-row-handle';
      handle.setAttribute('data-handle-type', type);
      handle.setAttribute('data-handle-index', String(index));

      const grip = document.createElement('span');
      grip.className = 'handle-grip';
      grip.innerHTML = type === 'col' ? GRIP_ICON_VERTICAL : GRIP_ICON_HORIZONTAL;
      handle.appendChild(grip);

      // 클릭 핸들러: 행/열 선택 + 메뉴 오픈 + 반대쪽 핸들 숨김
      handle.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const tablePos = findTablePos(editor, tableEl);
        if (tablePos === null) return;

        const menuType = type === 'col' ? 'column' : 'row';
        selectCells(editor, tablePos, menuType, index);

        // 반대쪽 핸들 숨기기 (열 클릭 → 행 핸들 숨김, 행 클릭 → 열 핸들 숨김)
        const oppositeClass = type === 'col' ? '.table-row-handle' : '.table-col-handle';
        wrapper.querySelectorAll<HTMLElement>(oppositeClass).forEach((h) => {
          h.removeAttribute('data-handle-visible');
        });

        const handleRect = handle.getBoundingClientRect();
        const direction: 'down' | 'up' =
          handleRect.bottom + 320 > window.innerHeight ? 'up' : 'down';

        const x = type === 'col'
          ? handleRect.left + handleRect.width / 2
          : handleRect.right + 4;
        const y = direction === 'down'
          ? (type === 'col' ? handleRect.bottom + 4 : handleRect.top)
          : (type === 'col' ? handleRect.top - 4 : handleRect.bottom);

        setMenuState({
          open: true,
          type: menuType,
          index,
          x,
          y,
          direction,
          tablePos,
        });
      });

      return handle;
    };

    /** 특정 tableWrapper에 대해 핸들 생성/갱신 */
    const syncHandles = (wrapper: HTMLElement) => {
      const scrollHost = wrapper.querySelector('.table-scroll') as HTMLElement | null;
      const table = wrapper.querySelector('table') as HTMLTableElement | null;
      if (!table) return;

      const host = scrollHost || table.parentElement!;
      const existingHandleCount = wrapper.getAttribute(HANDLES_ATTR);
      const rows = Array.from(table.querySelectorAll('tr'));
      const firstRow = rows[0];
      if (!firstRow) return;

      const firstRowCells = Array.from(firstRow.children) as HTMLElement[];
      const colCount = firstRowCells.length;
      const rowCount = rows.length;
      const handleId = `${colCount}-${rowCount}`;

      // 핸들 수가 같으면 위치만 업데이트
      if (existingHandleCount === handleId) {
        positionHandles(wrapper, table, host);
        return;
      }

      // 기존 핸들 제거
      wrapper.querySelectorAll('.table-col-handle, .table-row-handle').forEach((el) => el.remove());

      // 열 핸들 생성
      for (let i = 0; i < colCount; i++) {
        const handle = createHandle('col', i, table, wrapper);
        wrapper.appendChild(handle);
      }

      // 행 핸들 생성
      for (let i = 0; i < rowCount; i++) {
        const handle = createHandle('row', i, table, wrapper);
        wrapper.appendChild(handle);
      }

      wrapper.setAttribute(HANDLES_ATTR, handleId);
      positionHandles(wrapper, table, host);
    };

    const COL_HANDLE_W = 24; // CSS .table-col-handle width 와 동일
    const ROW_HANDLE_H = 24; // CSS .table-row-handle height 와 동일

    /** 핸들 위치 업데이트 — 셀 테두리 가운데에 고정 크기로 정렬
     *
     * 열 핸들: 1행 각 셀의 위쪽 테두리 중앙 (고정 24px 폭)
     * 행 핸들: 각 행의 1열 왼쪽 테두리 중앙 (고정 24px 높이)
     *
     * + 스크롤 영역 밖으로 나간 핸들은 숨김
     */
    const positionHandles = (
      wrapper: HTMLElement,
      table: HTMLTableElement,
      host: HTMLElement,
    ) => {
      const rows = Array.from(table.querySelectorAll('tr'));
      const firstRow = rows[0];
      if (!firstRow) return;

      const firstRowCells = Array.from(firstRow.children) as HTMLElement[];
      const wrapperRect = wrapper.getBoundingClientRect();
      const hostRect = host.getBoundingClientRect(); // .table-scroll 가시 영역

      // ── 열 핸들: 셀 위쪽 테두리 중앙, 고정 폭 ──
      const colHandles = wrapper.querySelectorAll<HTMLElement>('.table-col-handle');
      firstRowCells.forEach((cell, idx) => {
        const handle = colHandles[idx];
        if (!handle) return;
        const cellRect = cell.getBoundingClientRect();
        const centerX = cellRect.left + cellRect.width / 2;
        const handleLeft = centerX - COL_HANDLE_W / 2;
        const handleRight = handleLeft + COL_HANDLE_W;

        handle.style.left = `${handleLeft - wrapperRect.left}px`;
        handle.style.top = `${cellRect.top - wrapperRect.top}px`;

        // 스크롤 영역 밖이면 숨김
        handle.style.visibility =
          (handleRight < hostRect.left || handleLeft > hostRect.right) ? 'hidden' : '';
      });

      // ── 행 핸들: 1열 셀 왼쪽 테두리 중앙, 고정 높이 ──
      const rowHandles = wrapper.querySelectorAll<HTMLElement>('.table-row-handle');
      rows.forEach((row, idx) => {
        const handle = rowHandles[idx];
        if (!handle) return;
        const rowRect = row.getBoundingClientRect();
        const firstCell = row.querySelector('td, th') as HTMLElement | null;
        if (!firstCell) return;
        const firstCellRect = firstCell.getBoundingClientRect();
        const centerY = rowRect.top + rowRect.height / 2;

        handle.style.left = `${firstCellRect.left - wrapperRect.left}px`;
        handle.style.top = `${centerY - ROW_HANDLE_H / 2 - wrapperRect.top}px`;

        // 1열 좌측 테두리가 스크롤 영역 밖이면 숨김
        handle.style.visibility =
          (firstCellRect.left < hostRect.left - 3) ? 'hidden' : '';
      });
    };

    /** 모든 테이블 핸들 동기화 */
    const syncAllHandles = () => {
      const wrappers = editorDom.querySelectorAll<HTMLElement>('.tableWrapper');
      wrappers.forEach(syncHandles);
    };

    // 초기 실행 + 에디터 변경 시
    let rafId = requestAnimationFrame(syncAllHandles);
    const onUpdate = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(syncAllHandles);
    };
    editor.on('update', onUpdate);

    // DOM 변경 감지 (리사이즈 등)
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(syncAllHandles);
    });
    observer.observe(editorDom, {
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'colwidth', 'data-table-controls-active'],
    });

    // 윈도우 리사이즈 시 위치 재계산
    const onResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(syncAllHandles);
    };
    window.addEventListener('resize', onResize);

    // .table-scroll 가로 스크롤 시 핸들 위치/가시성 재계산
    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(syncAllHandles);
    };
    editorDom.addEventListener('scroll', onScroll, true);

    // ── 셀 포커스 추적: 클릭한 셀의 행/열 핸들만 표시 ──
    const VISIBLE_ATTR = 'data-handle-visible';
    const syncFocus = () => {
      const { $from } = editor.state.selection;
      let focusedRow = -1;
      let focusedCol = -1;
      let focusedWrapper: HTMLElement | null = null;

      // 셀렉션 위치에서 행/열 인덱스 추출
      for (let d = $from.depth; d >= 1; d--) {
        const node = $from.node(d);
        if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
          focusedCol = $from.index(d - 1); // 부모(row) 안에서 셀의 인덱스
        }
        if (node.type.name === 'tableRow') {
          focusedRow = $from.index(d - 1); // 부모(table) 안에서 행의 인덱스
        }
        if (node.type.name === 'table') {
          const tableDOM = editor.view.nodeDOM($from.before(d));
          if (tableDOM instanceof HTMLElement) {
            focusedWrapper = tableDOM.closest('.tableWrapper') as HTMLElement | null;
          }
          break;
        }
      }

      // 모든 핸들 숨기고, 해당하는 것만 표시
      editorDom.querySelectorAll<HTMLElement>(`.table-col-handle, .table-row-handle`).forEach((h) => {
        h.removeAttribute(VISIBLE_ATTR);
      });

      if (focusedWrapper && focusedRow >= 0 && focusedCol >= 0) {
        const colHandle = focusedWrapper.querySelector<HTMLElement>(
          `.table-col-handle[data-handle-index="${focusedCol}"]`,
        );
        const rowHandle = focusedWrapper.querySelector<HTMLElement>(
          `.table-row-handle[data-handle-index="${focusedRow}"]`,
        );
        if (colHandle) colHandle.setAttribute(VISIBLE_ATTR, 'true');
        if (rowHandle) rowHandle.setAttribute(VISIBLE_ATTR, 'true');
      }
    };

    const onSelectionUpdate = () => syncFocus();
    editor.on('selectionUpdate', onSelectionUpdate);
    editor.on('update', onSelectionUpdate);
    // 초기 실행
    syncFocus();

    return () => {
      cancelAnimationFrame(rafId);
      editor.off('update', onUpdate);
      editor.off('update', onSelectionUpdate);
      editor.off('selectionUpdate', onSelectionUpdate);
      observer.disconnect();
      window.removeEventListener('resize', onResize);
      editorDom.removeEventListener('scroll', onScroll, true);
    };
  }, [editor]);

  /* ── 메뉴 아이템 생성 ── */
  const getMenuItems = useCallback((): DropdownMenuItem[] => {
    if (!editor || !menuState.open) return [];

    const { type, index, tablePos } = menuState;
    const isRow = type === 'row';
    const typeLabel = isRow ? '행' : '열';

    /* 텍스트 색상 변경 */
    const applyTextColor = (color: string) => {
      const positions = getCellPositions(editor, tablePos, type, index);
      const { tr } = editor.state;
      let modified = false;

      positions.forEach((cellPos) => {
        const cell = tr.doc.nodeAt(cellPos);
        if (!cell) return;
        const from = cellPos + 1;
        const to = cellPos + cell.nodeSize - 1;
        if (from >= to) return;

        if (color) {
          const markType = editor.schema.marks.textStyle;
          if (markType) {
            tr.addMark(from, to, markType.create({ color }));
            modified = true;
          }
        } else {
          const markType = editor.schema.marks.textStyle;
          if (markType) {
            tr.removeMark(from, to, markType);
            modified = true;
          }
        }
      });

      if (modified) {
        editor.view.dispatch(tr);
      }
    };

    /* 배경 색상 변경 */
    const applyBgColor = (color: string) => {
      const positions = getCellPositions(editor, tablePos, type, index);
      const { tr } = editor.state;

      positions.forEach((cellPos) => {
        const cell = tr.doc.nodeAt(cellPos);
        if (!cell) return;
        tr.setNodeMarkup(cellPos, undefined, {
          ...cell.attrs,
          backgroundColor: color || null,
        });
      });

      editor.view.dispatch(tr);
    };

    /* 색상 서브메뉴 아이템 */
    const textColorItems: DropdownMenuItem[] = TEXT_COLORS.map((tc) => ({
      id: `tc-${tc.id}`,
      label: tc.label,
      icon: createElement(TextColorIcon, { color: tc.color }),
      onClick: () => applyTextColor(tc.color),
    }));

    const bgColorItems: DropdownMenuItem[] = BG_COLORS.map((bc) => ({
      id: `bg-${bc.id}`,
      label: bc.label,
      icon: createElement(BgColorIcon, { color: bc.color }),
      onClick: () => applyBgColor(bc.color),
    }));

    const colorChildren: DropdownMenuItem[] = [
      { id: 'section-text', label: '텍스트 색상', isSection: true, onClick: () => {} },
      ...textColorItems,
      { id: 'section-bg', label: '배경 색상', isSection: true, onClick: () => {} },
      ...bgColorItems,
    ];

    /* 복사 */
    const handleCopy = () => {
      const html = buildCopyHtml(editor, tablePos, type, index);
      if (!html) return;

      const tableNode = editor.state.doc.nodeAt(tablePos);
      if (!tableNode) return;

      // 일반 텍스트도 함께 복사
      let plainText = '';
      let rowOffset = tablePos + 1;
      for (let rowIdx = 0; rowIdx < tableNode.childCount; rowIdx++) {
        const row = tableNode.child(rowIdx);
        const cells: string[] = [];
        let cellOffset = rowOffset + 1;
        for (let colIdx = 0; colIdx < row.childCount; colIdx++) {
          const cell = row.child(colIdx);
          const isTarget =
            (type === 'row' && rowIdx === index) ||
            (type === 'column' && colIdx === index);
          if (isTarget) {
            cells.push(cell.textContent);
          }
          cellOffset += cell.nodeSize;
        }
        if (cells.length > 0) {
          plainText += cells.join('\t') + '\n';
        }
        rowOffset += row.nodeSize;
      }

      try {
        const htmlBlob = new Blob([html], { type: 'text/html' });
        const textBlob = new Blob([plainText.trimEnd()], { type: 'text/plain' });
        const clipboardItem = new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob,
        });
        navigator.clipboard.write([clipboardItem]);
      } catch {
        // fallback
        navigator.clipboard.writeText(plainText.trimEnd());
      }
    };

    /* 콘텐츠 삭제 */
    const handleDeleteContent = () => {
      const positions = getCellPositions(editor, tablePos, type, index);
      const { tr } = editor.state;

      positions.forEach((cellPos) => {
        const cell = tr.doc.nodeAt(cellPos);
        if (!cell) return;
        const from = cellPos + 1;
        const to = cellPos + cell.nodeSize - 1;
        if (from < to) {
          tr.delete(from, to);
          // 빈 paragraph 삽입
          const paragraphNode = editor.state.schema.nodes.paragraph.create();
          tr.insert(tr.mapping.map(from), paragraphNode);
        }
      });

      editor.view.dispatch(tr);
    };

    /* 삽입 */
    const handleInsertBefore = () => {
      const positions = getCellPositions(editor, tablePos, type, index);
      if (positions.length === 0) return;
      editor.chain().focus(positions[0] + 1).run();
      if (isRow) {
        editor.chain().addRowBefore().run();
      } else {
        editor.chain().addColumnBefore().run();
      }
    };

    const handleInsertAfter = () => {
      const positions = getCellPositions(editor, tablePos, type, index);
      if (positions.length === 0) return;
      editor.chain().focus(positions[0] + 1).run();
      if (isRow) {
        editor.chain().addRowAfter().run();
      } else {
        editor.chain().addColumnAfter().run();
      }
    };

    /* 삭제 */
    const handleDelete = () => {
      const positions = getCellPositions(editor, tablePos, type, index);
      if (positions.length === 0) return;
      editor.chain().focus(positions[0] + 1).run();
      if (isRow) {
        editor.chain().deleteRow().run();
      } else {
        editor.chain().deleteColumn().run();
      }
    };

    return [
      {
        id: 'color',
        label: '색',
        icon: createElement(Palette, { size: 15 }),
        onClick: () => {},
        children: colorChildren,
      },
      {
        id: 'insert-before',
        label: isRow ? '위에 삽입' : '왼쪽에 삽입',
        icon: isRow
          ? createElement(ArrowUp, { size: 15 })
          : createElement(ArrowLeft, { size: 15 }),
        onClick: handleInsertBefore,
      },
      {
        id: 'insert-after',
        label: isRow ? '아래에 삽입' : '오른쪽에 삽입',
        icon: isRow
          ? createElement(ArrowDown, { size: 15 })
          : createElement(ArrowRight, { size: 15 }),
        onClick: handleInsertAfter,
      },
      {
        id: 'copy',
        label: '복제',
        icon: createElement(Copy, { size: 15 }),
        onClick: handleCopy,
      },
      {
        id: 'delete-content',
        label: '콘텐츠 삭제',
        icon: createElement(XCircle, { size: 15 }),
        onClick: handleDeleteContent,
      },
      {
        id: 'delete',
        label: `${typeLabel} 삭제`,
        icon: createElement(Trash2, { size: 15 }),
        onClick: handleDelete,
        danger: true,
      },
    ];
  }, [editor, menuState]);

  return { cellMenuState: menuState, closeCellMenu: closeMenu, getCellMenuItems: getMenuItems };
}
